import {
  GetMessagesWithHardcopyFlagReportRequest,
  GetOlmStartEndMessageCountChartDataRequest,
  GetQualifiedAppliedMessageChartDataRequest,
  IGetMessagesWithHardcopyFlagReportRequest,
  IGetMessagesWithHardcopyFlagReportResponse,
  IGetOlmStartEndMessageCountChartDataRequest,
  IGetOlmStartEndMessageCountChartDataResponse,
  IGetQualifiedAppliedMessageChartDataRequest,
  IGetNonQualifiedMessageChartDataRequest,
  QualifiedAppliedMessagesChartDto,
  IGetSelectivityChangesReportRequest,
  IGetSelectivityChangesReportResponse,
  NonQualifiedMessageDto,
  //  NonQualifiedDetailsDto,
  GetSelectivityChangesReportRequest,
  NonQualifiedMessagesChartDto,
  GetNonQualifiedMessageChartDataRequest,
  IGetNonQualifiedMessageReportRequest,
  IGetNonQualifiedMessageReportResponse,
  GetNonQualifiedMessageReportRequest,
  IMessageWithHardcopyDto,
  IMessagesWithSelectivityChange,
} from '@/api/client';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IRequestState } from './interfaces';
import Api from '@/api/helpers';
import { State } from '@progress/kendo-data-query';

export interface ILandingState {
  qualifiedAppliedMessagesChartData: { qualifiedAppliedCounters: QualifiedAppliedMessagesChartDto[] } & IRequestState;

  nonQualifiedMessagesChartData: { nonQualifiedCounters: NonQualifiedMessagesChartDto[] } & IRequestState;

  startingEndingSoonMessagesData: {
    startingEndingCounters: IGetOlmStartEndMessageCountChartDataResponse;
  } & IRequestState;

  selectedMessagesNonQualifiedBar: { category: string; value: number | undefined };

  messagesWithSelectivityChanges: {
    messagesWithSelChange: IMessagesWithSelectivityChange[];
    gridDataState: State;
  } & IRequestState;

  nonQualifiedDetails: {
    nonQualifiedDetails: NonQualifiedMessageDto[];
    gridDataState: State;
  } & IRequestState;

  hardCopyMessagesData: {
    messagesWithHardCopy: IMessageWithHardcopyDto[];
    gridDataState: State;
  } & IRequestState;
}

const initialState: ILandingState = {
  qualifiedAppliedMessagesChartData: {
    qualifiedAppliedCounters: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  nonQualifiedMessagesChartData: {
    nonQualifiedCounters: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  startingEndingSoonMessagesData: {
    startingEndingCounters: {
      startingSoonMessageCount: 0,
      endingSoonMessageCount: 0,
      totalMessageCount: 0,
    },
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  selectedMessagesNonQualifiedBar: {
    category: '',
    value: undefined,
  },
  messagesWithSelectivityChanges: {
    messagesWithSelChange: [],
    gridDataState: { skip: 0, take: 5 },
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  nonQualifiedDetails: {
    nonQualifiedDetails: [],
    gridDataState: { skip: 0, take: 5 },
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  hardCopyMessagesData: {
    messagesWithHardCopy: [],
    gridDataState: { skip: 0, take: 5 },
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
};

const LandingSlice = createSlice({
  name: 'landing',
  initialState,
  reducers: {
    setSelectedMessagesNonQualifiedBar: (
      state,
      action: PayloadAction<{ category: string; value: number | undefined }>,
    ) => {
      state.selectedMessagesNonQualifiedBar = action.payload;
    },
    setHardcopyMessagesGridDataState: (state, action: PayloadAction<State>) => {
      state.hardCopyMessagesData.gridDataState = {
        filter: action.payload.filter,
        group: action.payload.group,
        sort: action.payload.sort,
        skip: action.payload.skip,
        take: action.payload.take,
      };
    },
    setMessagesWithSelectivityChangesGridDataState: (state, action: PayloadAction<State>) => {
      state.messagesWithSelectivityChanges.gridDataState = {
        filter: action.payload.filter,
        group: action.payload.group,
        sort: action.payload.sort,
        skip: action.payload.skip,
        take: action.payload.take,
      };
    },
    setNonQualifiedDetailsGridDataState: (state, action: PayloadAction<State>) => {
      state.nonQualifiedDetails.gridDataState = {
        filter: action.payload.filter,
        group: action.payload.group,
        sort: action.payload.sort,
        skip: action.payload.skip,
        take: action.payload.take,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchQualifiedAppliedMessagesChartData.pending, (state) => {
      state.qualifiedAppliedMessagesChartData.isRequestActive = true;
    });
    builder.addCase(fetchQualifiedAppliedMessagesChartData.fulfilled, (state, action) => {
      state.qualifiedAppliedMessagesChartData.isRequestActive = false;
      state.qualifiedAppliedMessagesChartData.qualifiedAppliedCounters = action.payload;
    });
    builder.addCase(fetchQualifiedAppliedMessagesChartData.rejected, (state) => {
      state.qualifiedAppliedMessagesChartData.isRequestActive = false;
    });

    builder.addCase(fetchNonQualifiedMessagesChartData.pending, (state) => {
      state.nonQualifiedMessagesChartData.isRequestActive = true;
    });
    builder.addCase(fetchNonQualifiedMessagesChartData.fulfilled, (state, action) => {
      state.nonQualifiedMessagesChartData.isRequestActive = false;
      state.nonQualifiedMessagesChartData.nonQualifiedCounters = action.payload;
    });
    builder.addCase(fetchNonQualifiedMessagesChartData.rejected, (state) => {
      state.nonQualifiedMessagesChartData.isRequestActive = false;
    });

    builder.addCase(fetchStartingEndingSoonMessages.pending, (state) => {
      state.startingEndingSoonMessagesData.isRequestActive = true;
    });
    builder.addCase(fetchStartingEndingSoonMessages.fulfilled, (state, action) => {
      state.startingEndingSoonMessagesData.isRequestActive = false;
      state.startingEndingSoonMessagesData.startingEndingCounters = action.payload;
    });
    builder.addCase(fetchStartingEndingSoonMessages.rejected, (state) => {
      state.startingEndingSoonMessagesData.isRequestActive = false;
    });
    builder.addCase(fetchMessagesWithSelectivityChange.pending, (state) => {
      state.messagesWithSelectivityChanges.isRequestActive = true;
    });
    builder.addCase(fetchMessagesWithSelectivityChange.fulfilled, (state, action) => {
      state.messagesWithSelectivityChanges.isRequestActive = false;
      state.messagesWithSelectivityChanges.messagesWithSelChange = action.payload.selectivityChangesReport;
    });
    builder.addCase(fetchMessagesWithSelectivityChange.rejected, (state) => {
      state.messagesWithSelectivityChanges.isRequestActive = false;
    });
    builder.addCase(fetchNonQualifiedDetail.pending, (state) => {
      state.nonQualifiedDetails.isRequestActive = true;
    });
    builder.addCase(fetchNonQualifiedDetail.fulfilled, (state, action) => {
      state.nonQualifiedDetails.isRequestActive = false;
      state.nonQualifiedDetails.nonQualifiedDetails = action.payload.nonQualifiedMessages;
      //selectivityChangesReport
    });
    builder.addCase(fetchNonQualifiedDetail.rejected, (state) => {
      state.nonQualifiedDetails.isRequestActive = false;
    });

    builder.addCase(fetchMessagesWithHardCopyReport.pending, (state) => {
      state.hardCopyMessagesData.isRequestActive = true;
    });
    builder.addCase(fetchMessagesWithHardCopyReport.fulfilled, (state, action) => {
      state.hardCopyMessagesData.isRequestActive = false;
      state.hardCopyMessagesData.messagesWithHardCopy = action.payload.messagesWithHardcopyReport;
    });
    builder.addCase(fetchMessagesWithHardCopyReport.rejected, (state) => {
      state.hardCopyMessagesData.isRequestActive = false;
    });
  },
});

export const fetchNonQualifiedMessagesChartData = createAsyncThunk<
  NonQualifiedMessagesChartDto[],
  IGetNonQualifiedMessageChartDataRequest
>('/reporting/getNonQualifiedMessagesChartData', async (request, thunkApi) => {
  const client = Api.getReportingClient(thunkApi.dispatch);
  const response = await client.getNonQualifiedMessageChartData(new GetNonQualifiedMessageChartDataRequest(request));
  return response.nonQualifiedMessagesChartData;
});

export const fetchQualifiedAppliedMessagesChartData = createAsyncThunk<
  QualifiedAppliedMessagesChartDto[],
  IGetQualifiedAppliedMessageChartDataRequest
>('/reporting/getQualifiedAppliedMessagesChartData', async (request, thunkApi) => {
  const client = Api.getReportingClient(thunkApi.dispatch);
  const response = await client.getQualifiedAppliedMessageChartData(
    new GetQualifiedAppliedMessageChartDataRequest(request),
  );
  return response.qualifiedAppliedMessagesChartData;
});

export const fetchStartingEndingSoonMessages = createAsyncThunk<
  IGetOlmStartEndMessageCountChartDataResponse,
  IGetOlmStartEndMessageCountChartDataRequest
>('/reporting/getOlmStartEndMessagesCountChartData', async (request, thunkApi) => {
  const client = Api.getReportingClient(thunkApi.dispatch);
  const response = await client.getOlmStartEndMessageCountChartData(
    new GetOlmStartEndMessageCountChartDataRequest(request),
  );
  return response;
});

export const fetchMessagesWithSelectivityChange = createAsyncThunk<
  IGetSelectivityChangesReportResponse,
  IGetSelectivityChangesReportRequest
>('/reporting/getMessagesWithSelectivityChange', async (request, thunkApi) => {
  const client = Api.getReportingClient(thunkApi.dispatch);
  const response = await client.getSelectivityChangesReport(new GetSelectivityChangesReportRequest(request));
  return response;
});

export const fetchNonQualifiedDetail = createAsyncThunk<
  IGetNonQualifiedMessageReportResponse,
  IGetNonQualifiedMessageReportRequest
>('/reporting/getNonQualifiedDetail', async (request, thunkApi) => {
  const client = Api.getReportingClient(thunkApi.dispatch);
  const response = await client.getNonQualifiedMessageReport(new GetNonQualifiedMessageReportRequest(request));
  return response;
});

export const fetchMessagesWithHardCopyReport = createAsyncThunk<
  IGetMessagesWithHardcopyFlagReportResponse,
  IGetMessagesWithHardcopyFlagReportRequest
>('/reporting/getMessagesWithHardcopyFlagReport', async (request, thunkApi) => {
  const client = Api.getReportingClient(thunkApi.dispatch);
  const response = await client.getMessagesWithHardcopyFlagReport(
    new GetMessagesWithHardcopyFlagReportRequest(request),
  );
  return response;
});

export const {
  setSelectedMessagesNonQualifiedBar,
  setHardcopyMessagesGridDataState,
  setMessagesWithSelectivityChangesGridDataState,
  setNonQualifiedDetailsGridDataState,
} = LandingSlice.actions;

export default LandingSlice.reducer;
